import React from 'react'
import PropTypes from 'prop-types'
import exact from 'prop-types-exact'
import * as Yup from 'yup'
import * as Types from 'types'
import {
  AdditionalInformationField,
  RadioGroup,
  CheckboxGroup,
  Textarea,
  OptionalLabel,
} from 'components'
import { StepForm } from 'forms'
import {
  CONSIDERATION_OPTIONS,
  BOOLEAN_STRING_OPTIONS,
  STEP,
  APPLICATION_INFORMATION_QUESTIONS,
  NEW_TENANT_CERT_TYPES,
  FILTER_OUT_QUESTIONS_FOR_STATES,
  MI_CERTIFICATE_TYPE
} from 'config'
import classnames from 'classnames'
const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  asset: Types.asset.isRequired,
  certType: PropTypes.string.isRequired,
}

const defaultProps = {}

function ApplicationInformationForm({
  asset,
  onSubmit,
  initialValues,
  certType,
}) {
  const isNewTenant = NEW_TENANT_CERT_TYPES.includes(certType)
  const skipLegalAndMarketing = certType !== MI_CERTIFICATE_TYPE
  const includeCriminalHistoryQuestions =
    FILTER_OUT_QUESTIONS_FOR_STATES.includes(asset.state)

  // Indices after bedroomSizes removal (was at index 3, shifted everything above by -1):
  // [0] handicapAccessible, [1] visualHearingImpairment, [2] otherRequestsComments
  // [3] pets, [4] serviceAnimal, [5] expectHouseholdAdditions, [6] minorsAbsentHalfTime
  // [7] expectAdoptions, [8] registeredSexOffender, [9] crimeConvictions, [10] evicted
  // [11] displaced, [12] homeless, [13] veterans, [14] domesticViolence
  // [15] legalMovedOutEarly ... [21] legalComments
  // [22] doesTheHouseholdHaveAVehicle, [23] howManyVehicles, [24] vehiclesComments
  // [25] marketingOnlineSearch, [26] marketingReferralFromAnotherPerson
  // [27] marketingSocialMedia, [28] marketingOther
  const legalQuestions = APPLICATION_INFORMATION_QUESTIONS.slice(15, 22)

  const getVehicleQuestions = (values) => {
    return APPLICATION_INFORMATION_QUESTIONS.slice(22, 25).filter(question => {
      if (question.name === 'howManyVehicles' || question.name === 'vehiclesComments') {
        return values.tenantApplication.doesTheHouseholdHaveAVehicle === 'Yes'
      }

      return true
    })
  }

  const getNewTenantSchemaValidation = () => {
    const validation = Yup.string()
    if (!isNewTenant) return validation
    if (includeCriminalHistoryQuestions) return validation

    const errorMessage = 'An answer is required'
    return validation.required(errorMessage)
  }

  const rentalHistoryQuestions = () => {
    // For NJ (includeCriminalHistoryQuestions): skip crimeConvictions, show only evicted [10]
    if (includeCriminalHistoryQuestions) {
      return APPLICATION_INFORMATION_QUESTIONS.slice(10, 11)
    }
    // Non-NJ: show crimeConvictions [9] and evicted [10]
    return APPLICATION_INFORMATION_QUESTIONS.slice(9, 11)
  }

  const marketingQuestionsSchema = {
    marketingOnlineSearch: Yup.string().required('An answer is required'),
    marketingOnlineSearchComments: Yup.string(),
    marketingReferralFromAnotherPerson: Yup.string().required('An answer is required'),
    marketingReferralComments: Yup.string(),
    marketingSocialMedia: Yup.string().required('An answer is required'),
    marketingSocialMediaComments: Yup.string(),
    marketingOther: Yup.string().required('An answer is required'),
    marketingOtherComments: Yup.string(),
  }

  const validationSchema = Yup.object({
    tenantApplication: Yup.object({
      crimeConvictions: getNewTenantSchemaValidation(),
      crimeConvictionsComments: Yup.string().when('crimeConvictions', {
        is: 'Yes',
        then: (schema) =>
          schema.required('Household member name and status required'),
      }),
      displaced: getNewTenantSchemaValidation(),
      displacedComments: Yup.string(),
      domesticViolence: getNewTenantSchemaValidation(),
      domesticViolenceComments: Yup.string(),
      evicted: getNewTenantSchemaValidation(),
      evictedComments: Yup.string(),
      expectAdoptions: Yup.string().required('An answer is required'),
      expectAdoptionsComments: Yup.string(),
      expectHouseholdAdditions: Yup.string().required('An answer is required'),
      expectHouseholdAdditionsComments: Yup.string(),
      handicapAccessible: Yup.string().optional(),
      homeless: getNewTenantSchemaValidation(),
      homelessComments: Yup.string(),
      minorsAbsentHalfTime: Yup.string().required('An answer is required'),
      minorsAbsentHalfTimeComments: Yup.string(),
      otherRequestsComments: Yup.string(),
      pets: Yup.string().required('An answer is required'),
      petsComments: Yup.string(),
      registeredSexOffender: Yup.string().required('An answer is required'),
      registeredSexOffenderComments: Yup.string().when(
        'registeredSexOffender',
        {
          is: 'Yes',
          then: (schema) =>
            schema.required(
              'Household member name and status and all states resided in are required'
            ),
        }
      ),
      serviceAnimal: Yup.string().required('An answer is required'),
      serviceAnimalComments: Yup.string(),
      veterans: getNewTenantSchemaValidation(),
      veteransComments: Yup.string(),
      visualHearingImpairment: getNewTenantSchemaValidation(),
      visualHearingImpairmentComments: Yup.string(),
      // If any of the legal questions are answered yes, then the legalComments field is required
      legalComments: skipLegalAndMarketing
        ? Yup.string()
        : Yup.string().when(
            [
              'legalMovedOutEarly',
              'legalBankruptcy',
              'legalUnpaidRent',
              'legalBeenSuedForPropertyDamage',
              'legalBeenEvicted',
              'legalConvictedOfAMisdemeanor'
            ],
            {
              is: (...fields) => fields.some(val => val === 'Yes'),
              then: (schema) => schema.required('Legal comments are required'),
              otherwise: (schema) => schema
            }
          ),
      doesTheHouseholdHaveAVehicle: Yup.string().required('An answer is required'),
      howManyVehicles: Yup.array().of(Yup.string()).when('doesTheHouseholdHaveAVehicle', {
        is: 'Yes',
        then: (schema) => schema.required('Please provide the number of vehicles'),
      }),
      vehiclesComments: Yup.string().when('doesTheHouseholdHaveAVehicle', {
        is: 'Yes',
        then: (schema) => schema.required('Information on vehicles is required'),
      }),
      ...(!skipLegalAndMarketing && marketingQuestionsSchema),
    }),
  })

  const filteredQuestionIndices = () => {
    return APPLICATION_INFORMATION_QUESTIONS.reduce((result, _, index) => {
      if (!isNewTenant) {
        if (index >= 0 && index <= 2) return result // Remove unit request questions (new tenant only)
        if (index >= 10 && index <= 15) return result // Remove rental history / other considerations
      }

      if (!(asset.handicapUnitsCount > 0)) {
        if (index === 1) return result // Remove visualHearingImpairment question when no handicap units
      }

      // For NJ, crimeConvictions (index 9) is not shown — exclude from numbering
      if (includeCriminalHistoryQuestions && index == 9) {
        return result
      }

      result.push(index)
      return result
    }, [])
  }

  const questionIndices = filteredQuestionIndices()

  return (
    <StepForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      previousStep={STEP.CONTACT_INFORMATION}
    >
      {({ values }) => (
        <>
          <div className="form-section">
            <div className="heading-wrapper">
              <h2>Unit Requests</h2>
            </div>
            {isNewTenant && asset.handicapUnitsCount > 0 && (
              <div className="row bottom-border">
                <div className="col-12">
                  <RadioGroup
                    name="tenantApplication.handicapAccessible"
                    label={`${questionIndices.indexOf(0)}. ${APPLICATION_INFORMATION_QUESTIONS[0].label
                      }`}
                    options={BOOLEAN_STRING_OPTIONS}
                    required
                  />
                </div>
              </div>
            )}
            {isNewTenant && (
              <AdditionalInformationField
                name="tenantApplication.visualHearingImpairment"
                label={`${questionIndices.indexOf(1)}. ${APPLICATION_INFORMATION_QUESTIONS[1].label
                  }`}
                additionalLabelComponent={OptionalLabel}
                additionalName="tenantApplication.visualHearingImpairmentComments"
                answer={values.tenantApplication.visualHearingImpairment}
              />
            )}
            <div className="row bottom-border">
              <div className="col-12">
                <Textarea
                  name="tenantApplication.otherRequestsComments"
                  label={`${questionIndices.indexOf(2)}. ${APPLICATION_INFORMATION_QUESTIONS[2].label
                    }`}
                  additionalLabelComponent={OptionalLabel}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="heading-wrapper">
              <h2>Pets and Service/Support Animals</h2>
            </div>
            <AdditionalInformationField
              name="tenantApplication.pets"
              label={`1. ${APPLICATION_INFORMATION_QUESTIONS[3].label}`}
              additionalLabelComponent={OptionalLabel}
              additionalName="tenantApplication.petsComments"
              answer={values.tenantApplication.pets}
            />
            <AdditionalInformationField
              name="tenantApplication.serviceAnimal"
              label={`2. ${APPLICATION_INFORMATION_QUESTIONS[4].label}`}
              additionalLabelComponent={OptionalLabel}
              additionalName="tenantApplication.serviceAnimalComments"
              answer={values.tenantApplication.serviceAnimal}
            />
          </div>

          <div className="form-section">
            <div className="heading-wrapper">
              <h2>Household Members</h2>
            </div>
            <AdditionalInformationField
              name="tenantApplication.expectHouseholdAdditions"
              label={`1. ${APPLICATION_INFORMATION_QUESTIONS[5].label}`}
              additionalLabelComponent={OptionalLabel}
              additionalName="tenantApplication.expectHouseholdAdditionsComments"
              answer={values.tenantApplication.expectHouseholdAdditions}
            />
            <AdditionalInformationField
              name="tenantApplication.minorsAbsentHalfTime"
              label={`2. ${APPLICATION_INFORMATION_QUESTIONS[6].label}`}
              additionalLabelComponent={OptionalLabel}
              additionalName="tenantApplication.minorsAbsentHalfTimeComments"
              answer={values.tenantApplication.minorsAbsentHalfTime}
            />
            <AdditionalInformationField
              name="tenantApplication.expectAdoptions"
              label={`3. ${APPLICATION_INFORMATION_QUESTIONS[7].label}`}
              additionalLabelComponent={OptionalLabel}
              additionalName="tenantApplication.expectAdoptionsComments"
              answer={values.tenantApplication.expectAdoptions}
            />
            <AdditionalInformationField
              name="tenantApplication.registeredSexOffender"
              label={`4. ${APPLICATION_INFORMATION_QUESTIONS[8].label}`}
              additionalLabel="Please state the household member name and status and all states resided in"
              additionalName="tenantApplication.registeredSexOffenderComments"
              answer={values.tenantApplication.registeredSexOffender}
              additionalRequired
            />
          </div>

          {isNewTenant && (
            <div className="form-section">
              <div className="heading-wrapper">
                <h2>Rental History</h2>
              </div>
              {rentalHistoryQuestions().map((question, index) => (
                <AdditionalInformationField
                  key={`question-${question.name}-${index}`}
                  name={`tenantApplication.${question.name}`}
                  label={`${index + 1}. ${question.label}`}
                  additionalLabel={question.additionalLabel}
                  additionalName={`tenantApplication.${question.additionalName}`}
                  additionalLabelComponent={
                    !question.additionalRequired && OptionalLabel
                  }
                  answer={`${values.tenantApplication[question.name]}`}
                  additionalRequired={question.additionalRequired}
                />
              ))}
            </div>
          )}

          {!skipLegalAndMarketing && (
            <div className="form-section">
              <div className="heading-wrapper">
                <h2>Rental and Criminal History</h2>
              </div>
              {legalQuestions.map((question, index) => (
                <div
                  key={`tenantApplication.${question.name}`}
                  className={classnames('row', {
                    'bottom-border': index === legalQuestions.length - 2
                  })}
                >
                  {question.name == 'legalComments' ? (
                    <div className="col-12">
                      <Textarea name={`tenantApplication.${question.name}`} label={`${index + 1}. ${question.label}`} />
                    </div>
                  ) : (
                    <div className="col-12">
                      <RadioGroup name={`tenantApplication.${question.name}`} label={`${index + 1}. ${question.label}`} options={BOOLEAN_STRING_OPTIONS} required />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="form-section">
            <div className="heading-wrapper">
              <h2>Vehicles</h2>
            </div>
            {getVehicleQuestions(values).map((question, index) => (
              <div
                key={`tenantApplication.${question.name}`}
                className={classnames('row', {
                  'bottom-border': index === getVehicleQuestions(values).length - 2
                })}
              >
                {question.name == 'vehiclesComments' ? (
                  <div className="col-12">
                    <Textarea name={`tenantApplication.${question.name}`} label={`${index + 1}. ${question.label}`} />
                  </div>
                ) : (
                  <>
                    {question.name == 'howManyVehicles' ? (
                      <div className="col-12">
                        <CheckboxGroup
                          name="tenantApplication.howManyVehicles"
                          label={`${index + 1}. ${question.label}`}
                          options={['1 Vehicle', '2 Vehicles', '3 Vehicles', '4 Vehicles', '5 Vehicles', 'More than 5 Vehicles']}
                          required
                        />
                      </div>
                    ) : (
                      <div className="col-12">
                        <RadioGroup name={`tenantApplication.${question.name}`} label={`${index + 1}. ${question.label}`} options={BOOLEAN_STRING_OPTIONS} required />
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {!skipLegalAndMarketing && (
            <div className="form-section">
              <div className="heading-wrapper">
                <h2>Marketing - How did you find us?</h2>
              </div>
              <AdditionalInformationField
                name="tenantApplication.marketingOnlineSearch"
                label={`1. ${APPLICATION_INFORMATION_QUESTIONS[25].label}`}
                options={BOOLEAN_STRING_OPTIONS}
                answer={values.tenantApplication.marketingOnlineSearch}
                additionalName="tenantApplication.marketingOnlineSearchComments"
                additionalLabelComponent={OptionalLabel}
              />
              <AdditionalInformationField
                name="tenantApplication.marketingReferralFromAnotherPerson"
                label={`2. ${APPLICATION_INFORMATION_QUESTIONS[26].label}`}
                options={BOOLEAN_STRING_OPTIONS}
                answer={values.tenantApplication.marketingReferralFromAnotherPerson}
                additionalName="tenantApplication.marketingReferralComments"
                additionalLabelComponent={OptionalLabel}
              />
              <AdditionalInformationField
                name="tenantApplication.marketingSocialMedia"
                label={`3. ${APPLICATION_INFORMATION_QUESTIONS[27].label}`}
                options={BOOLEAN_STRING_OPTIONS}
                answer={values.tenantApplication.marketingSocialMedia}
                additionalName="tenantApplication.marketingSocialMediaComments"
                additionalLabelComponent={OptionalLabel}
              />
              <AdditionalInformationField
                name="tenantApplication.marketingOther"
                label={`4. ${APPLICATION_INFORMATION_QUESTIONS[28].label}`}
                options={BOOLEAN_STRING_OPTIONS}
                answer={values.tenantApplication.marketingOther}
                additionalName="tenantApplication.marketingOtherComments"
                additionalLabelComponent={OptionalLabel}
              />
            </div>
          )}

          {isNewTenant && (
            <div className="form-section">
              <div className="heading-wrapper">
                <h2>Other Considerations</h2>
              </div>
              <AdditionalInformationField
                name="tenantApplication.displaced"
                label={`1. ${APPLICATION_INFORMATION_QUESTIONS[11].label}`}
                additionalLabelComponent={OptionalLabel}
                additionalName="tenantApplication.displacedComments"
                options={CONSIDERATION_OPTIONS}
                answer={values.tenantApplication.displaced}
              />
              <AdditionalInformationField
                name="tenantApplication.homeless"
                label={`2. ${APPLICATION_INFORMATION_QUESTIONS[12].label}`}
                additionalLabelComponent={OptionalLabel}
                additionalName="tenantApplication.homelessComments"
                options={CONSIDERATION_OPTIONS}
                answer={values.tenantApplication.homeless}
              />
              <AdditionalInformationField
                name="tenantApplication.veterans"
                label={`3. ${APPLICATION_INFORMATION_QUESTIONS[13].label}`}
                additionalLabelComponent={OptionalLabel}
                additionalName="tenantApplication.veteransComments"
                options={CONSIDERATION_OPTIONS}
                answer={values.tenantApplication.veterans}
              />
              <AdditionalInformationField
                name="tenantApplication.domesticViolence"
                label={`4. ${APPLICATION_INFORMATION_QUESTIONS[14].label}`}
                additionalLabelComponent={OptionalLabel}
                additionalName="tenantApplication.domesticViolenceComments"
                options={CONSIDERATION_OPTIONS}
                answer={values.tenantApplication.domesticViolence}
              />
            </div>
          )}
        </>
      )}
    </StepForm>
  )
}

ApplicationInformationForm.propTypes = exact(propTypes)
ApplicationInformationForm.defaultProps = defaultProps

export default React.memo(ApplicationInformationForm)
